import _ from "lodash";
import PositionHelper from "./position_helper";

class AnnotationGenerator {
  static Annotation = class {
    type = null;
    text = null;
    position = null;
    altPosition = null;
    dimensions = null;
    object = null;
    allObjects = [];
    isAlt = false;

    constructor(data) {
      this.type = data.type;
      this.text = data.text;
      this.position = {
        x: Math.round(data.position.x),
        y: Math.round(data.position.y),
      };
      this.altPosition = {
        x: Math.round(data.altPosition.x),
        y: Math.round(data.altPosition.y),
      };
      this.dimensions = data.dimensions;
      this.object = data.object;
      this.allObjects = data.allObjects;
    }

    isHeight() {
      return this.type === "height";
    }

    isWidth() {
      return this.type === "width";
    }

    key() {
      return `${this.text}/${this.isHeight() ? "y" : "x"}/${this.isHeight() ? this.position.y : this.position.x}`;
    }
  };

  objects = [];
  size = 200;
  offsets = { module: 25, group: 25 + this.size, wall: 25 + 2 * this.size };
  annotations = [];

  constructor(objects) {
    this.objects = objects;
  }

  #populate() {
    this.annotations = [];

    this.objects.forEach((object) => {
      const bounds = PositionHelper.getBoundsOfObject(object);

      if (object.type !== "group") {
        this.annotations.push(
          new AnnotationGenerator.Annotation({
            type: "height",
            text: object.dimensions.height,
            position: {
              x: bounds.xMin - this.size - this.offsets[object.type],
              y: bounds.yMin,
            },
            altPosition: {
              x: bounds.xMax + this.offsets[object.type],
              y: bounds.yMin,
            },
            dimensions: { height: object.dimensions.height, width: this.size },
            object: object,
            allObjects: [object],
          })
        );
      }

      this.annotations.push(
        new AnnotationGenerator.Annotation({
          type: "width",
          text: object.dimensions.width,
          position: {
            x: bounds.xMin,
            y: bounds.yMin - this.size - this.offsets[object.type],
          },
          altPosition: {
            x: bounds.xMin,
            y: bounds.yMax + this.offsets[object.type],
          },
          dimensions: { height: this.size, width: object.dimensions.width },
          object: object,
          allObjects: [object],
        })
      );
    });
  }

  #optimize() {
    const similarAnnotations = _.groupBy(this.annotations, (a) => a.key());

    this.annotations.forEach((annotation) => {
      const aBounds = PositionHelper.getBoundsOfObject(annotation);
      const isOverlapping = this.objects.some((object) => {
        return PositionHelper.isOverlapping(
          aBounds,
          PositionHelper.getBoundsOfObject(object)
        );
      });

      if (isOverlapping) {
        // Mark as conflicted. We will find an alternative for this later.
        annotation.conflict = true;

        // Check whether the alt position is clear.
        const altBounds = PositionHelper.getBounds(
          annotation.altPosition,
          annotation.dimensions
        );
        const isAltOverlapping = this.objects.some((object) => {
          return PositionHelper.isOverlapping(
            altBounds,
            PositionHelper.getBoundsOfObject(object)
          );
        });

        annotation.altValid = !isAltOverlapping;
      }
    });

    this.annotations.forEach((annotation) => {
      if (annotation.conflict) {
        let otherAnnotations = similarAnnotations[annotation.key()];
        otherAnnotations = otherAnnotations.filter((a) => !a.conflict);
        let altAnnotation = null;

        if (otherAnnotations.length) {
          if (annotation.isHeight()) {
            // Take only annotations that are to the left of the current one.
            otherAnnotations = otherAnnotations.filter(
              (a) => a != annotation && a.position.x < annotation.position.x
            );
            // Sort from right-to-left so as to choose the right-most annotation.
            otherAnnotations = otherAnnotations.sort(
              (a, b) => a.position.x > b.position.x
            );
          } else {
            // Take only annotations that are to the bottom of the current one.
            otherAnnotations = otherAnnotations.filter(
              (a) => a != annotation && a.position.y < annotation.position.y
            );
            // Sort from top-to-bottom so as to choose the top-most annotation.
            otherAnnotations = otherAnnotations.sort(
              (a, b) => a.position.y > b.position.y
            );
          }

          altAnnotation = otherAnnotations[0];
        }

        if (altAnnotation) {
          altAnnotation.allObjects.push(annotation.object);
        } else {
          if (annotation.altValid) {
            // Alternate position is free. Use that as the position.
            annotation.position = annotation.altPosition;
            annotation.isAlt = true;
          } else {
            // No other position to render. Render at the conflicted point itself.
          }

          annotation.ignoreConflict = true;
        }
      }
    });
  }

  process() {
    this.#populate();
    this.#optimize();
  }

  getValidAnnotations() {
    return this.annotations.filter((a) => !a.conflict || a.ignoreConflict);
  }
}

export default AnnotationGenerator;
